/**
 * On-screen versions of the programme's plan sections (cover, at a glance, bar chart, goals,
 * daily routine, fridge sheet, tables, who can help, closing note). Mirrors the backend's
 * `src/pdf/html.ts` so the app and the PDF say the same thing. Motion is decorative only:
 * blocks rise in and bars grow, and `prefers-reduced-motion` switches all of it off (index.css).
 */
import type { CSSProperties } from 'react';
import { BandPill } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { useT } from '@/i18n';
import type { Block, ResultBand, RoleIcon } from '@/api/types';

type Of<K extends Block['kind']> = Extract<Block, { kind: K }>;

/** Staggered entrance: the n-th item starts a little after the one before. */
const rise = (i: number): CSSProperties => ({ animationDelay: `${Math.min(i, 8) * 70}ms` });

export const CoverBlock = ({ block }: { block: Of<'cover'> }) => {
  const t = useT();
  return (
    <div className="animate-rise space-y-5">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-lagoon-700 via-lagoon-600 to-lagoon-500 p-6 text-white nav:p-8">
        <span aria-hidden="true" className="absolute -right-10 -top-12 h-40 w-40 rounded-full bg-coral-500/20" />
        <span aria-hidden="true" className="absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-sage-500/20" />
        <p className="relative text-caption font-extrabold uppercase tracking-[0.14em] text-coral-300">
          {t('programme.cover.eyebrow', { week: block.week })}
        </p>
        <p className="relative mt-2 font-display text-display">{t('programme.cover.title', { name: block.name })}</p>
        <p className="relative mt-2 text-body text-lagoon-200">
          {t('programme.cover.tag')} · {block.name}, {block.ageLabel}
        </p>
        <span
          className={cx(
            'relative mt-4 inline-block rounded-pill px-3 py-1 text-label',
            block.reviewed ? 'bg-progress-soft text-progress-ink' : 'bg-attention-soft text-attention-ink',
          )}
        >
          {block.reviewed ? t('programme.cover.reviewed') : t('programme.cover.provisional')}
        </span>
      </div>
      <ul role="list" className="grid grid-cols-3 gap-3">
        {block.facts.map((f, i) => (
          <li key={f.label} role="listitem" className="animate-rise rounded-lg bg-white p-4 shadow-1" style={rise(i + 1)}>
            <p className="font-display text-h2 text-lagoon-700">{f.value}</p>
            <p className="text-caption uppercase tracking-wide text-ink-muted">{f.label}</p>
          </li>
        ))}
      </ul>
      {block.contents && block.contents.length > 0 && (
        <nav aria-label={t('programme.cover.contents')}>
          <h3>{t('programme.cover.contents')}</h3>
          <ul role="list" className="mt-2 grid gap-x-8 gap-y-1 nav:grid-cols-2">
            {block.contents.map((c) => (
              <li key={c.page} role="listitem">
                <a
                  href={`#report-page-${c.page}-heading`}
                  className="flex items-baseline justify-between gap-3 border-b border-dotted border-sage-300 py-1.5 text-body text-ink hover:text-lagoon-600"
                >
                  <span className="font-bold">{c.title}</span>
                  <span className="font-extrabold text-coral-600">{c.page}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

const STEPS: ResultBand[] = ['ok', 'watch', 'discuss'];

export const GlanceBlock = ({ block }: { block: Of<'glance'> }) => {
  const t = useT();
  return (
    <div role="table" aria-label={t('programme.glance.label')} className="space-y-2">
      <div role="row" className="hidden grid-cols-[2fr_repeat(3,1fr)] gap-2 px-4 text-caption uppercase tracking-wide text-ink-muted nav:grid">
        <span role="columnheader">{t('programme.glance.area')}</span>
        {STEPS.map((b) => (
          <span key={b} role="columnheader" className="text-center">
            {t(`band.${b}`)}
          </span>
        ))}
      </div>
      {block.areas.map((a, i) => (
        <div
          key={a.label}
          role="row"
          className="grid animate-rise grid-cols-[1fr_auto] items-center gap-2 rounded-lg bg-white p-3 shadow-1 nav:grid-cols-[2fr_repeat(3,1fr)] nav:px-4"
          style={rise(i)}
        >
          <span role="rowheader" className="text-label text-ink">
            {a.label}
            {a.fromReports && <span className="block text-caption text-ink-muted">{t('report.fromFormal')}</span>}
          </span>
          {STEPS.map((b) => (
            <span key={b} role="cell" className={cx('justify-center', b === a.band ? 'flex' : 'hidden nav:flex')}>
              {b === a.band ? (
                <BandPill band={a.band} label={a.bandLabel} />
              ) : (
                <span aria-hidden="true" className="h-8 w-full rounded-pill border border-dashed border-sage-300" />
              )}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

export const BarsBlock = ({ block }: { block: Of<'bars'> }) => {
  const t = useT();
  return (
    <figure className="rounded-lg bg-white p-4 shadow-1">
      <div className="relative space-y-3">
        {block.items.map((b, i) => (
          // Phones: the label sits above its bar; wider screens: label, bar and value on one row.
          <div key={b.label} className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 nav:grid-cols-[minmax(8rem,12rem)_1fr_auto]">
            <span className="col-span-2 text-label text-ink nav:col-span-1 nav:text-right">{b.label}</span>
            <span className="relative h-3.5 rounded-pill bg-sage-100">
              <span
                className={cx('absolute inset-y-0 left-0 origin-left animate-grow rounded-pill', b.alert ? 'bg-coral-500' : 'bg-lagoon-500')}
                style={{ width: `${Math.max(2, b.value)}%`, animationDelay: `${i * 90}ms` }}
              />
              <span
                aria-hidden="true"
                className="absolute -inset-y-1.5 w-0 border-l-2 border-dashed border-lagoon-700"
                style={{ left: `${block.reference}%` }}
              />
            </span>
            <span className="w-24 text-label text-ink">
              {b.value}%{b.alert && <span className="ml-1 text-caption text-coral-ink">· {t('programme.bars.focus')}</span>}
            </span>
          </div>
        ))}
      </div>
      <figcaption className="mt-3 text-caption text-ink-muted">
        {t('programme.bars.reference', { reference: block.reference })} {block.caption}
      </figcaption>
    </figure>
  );
};

export const GoalsBlock = ({ block }: { block: Of<'goals'> }) => {
  const t = useT();
  return (
    <ol className="space-y-3">
      {block.items.map((g, i) => (
        <li key={g.title} className="flex animate-rise gap-4 rounded-xl bg-white p-5 shadow-1" style={rise(i)}>
          <span aria-hidden="true" className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-coral-500 font-display text-h3 text-white">
            {i + 1}
          </span>
          <div className="space-y-1.5">
            <p>
              <span className="font-display text-h3 text-lagoon-700">{g.title}</span>
              <span className="ml-2 inline-block rounded-pill bg-lagoon-100 px-2 py-0.5 text-caption text-lagoon-700">{g.area}</span>
            </p>
            {g.now && <p className="text-body italic text-ink-muted">{g.now}</p>}
            <p className="text-body">
              <span className="mr-2 text-caption uppercase tracking-wide text-progress-ink">{t('programme.goals.target')}</span>
              {g.target}
            </p>
            <p className="text-body">
              <span className="mr-2 text-caption uppercase tracking-wide text-provider">{t('programme.goals.track')}</span>
              {g.measure}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
};

export const RoutineBlock = ({ block }: { block: Of<'routine'> }) => {
  const t = useT();
  return (
    <div className="space-y-4">
      <ol className="relative space-y-3 border-l-2 border-sage-300 pl-6">
        {block.rows.map((r, i) => (
          <li key={r.time} className="relative animate-rise" style={rise(i)}>
            <span aria-hidden="true" className="absolute -left-[33px] top-3 h-4 w-4 rounded-full border-4 border-coral-100 bg-coral-500" />
            <div className="rounded-lg bg-white p-4 shadow-1">
              <p className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-label text-lagoon-700">
                  {r.time} · <span className="text-ink">{r.what}</span>
                </span>
                {r.minutes ? <span className="text-caption text-ink-muted">{t('programme.routine.minutes', { n: r.minutes })}</span> : null}
              </p>
              {r.activities.length > 0 && (
                <ul role="list" className="mt-2 flex flex-wrap gap-1.5">
                  {r.activities.map((a) => (
                    <li key={a} role="listitem" className="rounded-pill bg-sage-100 px-2.5 py-0.5 text-caption text-sage-600">
                      {a}
                    </li>
                  ))}
                </ul>
              )}
              {r.note && <p className="mt-1.5 text-body text-ink-soft">{r.note}</p>}
            </div>
          </li>
        ))}
      </ol>
      <p className="flex items-center gap-3 rounded-xl bg-coral-100 p-4 text-label text-coral-ink">
        <span aria-hidden="true" className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-coral-500 text-white">
          ★
        </span>
        {block.reward}
      </p>
    </div>
  );
};

export const FridgeBlock = ({ block }: { block: Of<'fridge'> }) => {
  const t = useT();
  const head = ['when', 'say', 'help', 'then'] as const;
  return (
    <div className="overflow-hidden rounded-xl shadow-1">
      <div className="hidden grid-cols-4 gap-4 bg-lagoon-600 px-4 py-3 text-caption uppercase tracking-wide text-white nav:grid">
        {head.map((h) => (
          <span key={h}>{t(`programme.fridge.${h}`)}</span>
        ))}
      </div>
      {block.rows.map((r, i) => (
        <div key={r.situation} className={cx('grid gap-1 px-4 py-3 nav:grid-cols-4 nav:gap-4', i % 2 ? 'bg-white' : 'bg-sage-100')}>
          <p className="text-label text-lagoon-700">{r.situation}</p>
          <p className="text-body font-bold text-coral-ink">
            <span className="mr-1 text-caption uppercase text-ink-muted nav:hidden">{t('programme.fridge.say')}:</span>
            {r.say}
          </p>
          <p className="text-body">
            <span className="mr-1 text-caption uppercase text-ink-muted nav:hidden">{t('programme.fridge.help')}:</span>
            {r.instead}
          </p>
          <p className="text-body">
            <span className="mr-1 text-caption uppercase text-ink-muted nav:hidden">{t('programme.fridge.then')}:</span>
            {r.then}
          </p>
        </div>
      ))}
    </div>
  );
};

export const TableBlock = ({ block }: { block: Of<'table'> }) => (
  <div className="overflow-hidden rounded-lg bg-white shadow-1">
    <table className="w-full border-collapse text-left">
      <thead className="bg-lagoon-100 text-caption uppercase tracking-wide text-lagoon-700">
        <tr>
          {block.columns.map((c) => (
            <th key={c} scope="col" className="px-4 py-2.5">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.rows.map((r, i) => (
          <tr key={i} className={cx('border-t border-sage-200 align-top', block.tone === 'alert' && i === 0 && 'bg-alert-soft text-alert-ink')}>
            {r.map((c, j) => (
              <td key={j} className={cx('px-4 py-3 text-body', j === 0 && 'font-bold')}>
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ICON_PATHS: Record<RoleIcon, string> = {
  doctor: 'M6 3v5a4 4 0 0 0 8 0V3M10 12v3a4 4 0 0 0 8 0v-1M20 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0',
  hand: 'M8 12V6a1.5 1.5 0 0 1 3 0v5M11 11V4.5a1.5 1.5 0 0 1 3 0V11M14 11V6a1.5 1.5 0 0 1 3 0v7M17 13v-2a1.5 1.5 0 0 1 3 0v3a7 7 0 0 1-7 7h-1a6 6 0 0 1-5-2.7L4.3 15a1.5 1.5 0 0 1 2.5-1.7L8 15',
  speech: 'M4 5h16v10H10l-5 4v-4H4zM8 9h8M8 12h5',
  mind: 'M12 3a6 6 0 0 0-3.5 10.9V16h7v-2.1A6 6 0 0 0 12 3zM9.5 19.5h5M10.5 22h3',
  school: 'M2.5 9.5 12 5l9.5 4.5L12 14zM6.5 11.5V16c0 1.2 2.5 2.8 5.5 2.8s5.5-1.6 5.5-2.8v-4.5',
  people: 'M11.5 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0M3 19a5.5 5.5 0 0 1 11 0M19 9a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0M15 14.2A5 5 0 0 1 21 19',
  heart: 'M12 20s-7-4.4-7-9.5A3.8 3.8 0 0 1 12 8a3.8 3.8 0 0 1 7 2.5C19 15.6 12 20 12 20z',
};

export const RolesBlock = ({ block }: { block: Of<'roles'> }) => (
  <ul role="list" className="space-y-3">
    {block.items.map((r, i) => {
      const icon = r.icon ?? 'people';
      return (
        <li key={r.role} role="listitem" className="flex animate-rise gap-4 rounded-lg bg-white p-4 shadow-1" style={rise(i)}>
          <span
            aria-hidden="true"
            className={cx(
              'flex h-10 w-10 flex-none items-center justify-center rounded-md',
              icon === 'heart' ? 'bg-coral-100 text-coral-ink' : 'bg-lagoon-100 text-lagoon-700',
            )}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill={icon === 'heart' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d={ICON_PATHS[icon]} />
            </svg>
          </span>
          <div>
            <p className="text-label text-lagoon-700">{r.role}</p>
            <p className="text-body text-ink-soft">{r.why}</p>
            <p className="mt-1 text-body font-bold text-lagoon-600">{r.ask}</p>
          </div>
        </li>
      );
    })}
  </ul>
);

export const NoteBlock = ({ block }: { block: Of<'note'> }) => (
  <blockquote className="relative animate-rise rounded-xl border-l-8 border-coral-500 bg-white p-6 shadow-1 nav:p-8">
    <span aria-hidden="true" className="absolute right-6 top-2 font-display text-[64px] leading-none text-coral-100">
      “
    </span>
    {block.lines.map((l) => (
      <p key={l} className="mb-3 text-body leading-relaxed">
        {l}
      </p>
    ))}
    <footer className="mt-4 text-label text-lagoon-700">{block.signoff}</footer>
  </blockquote>
);
